import React from 'react';
import {
  Chip,
  ChipProps,
  IconButton,
  IconProps,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import * as MuiIcon from '@mui/icons-material';
import HeartIconContainer from './HeartIcon';
import { MAPPING_KEYS } from './constant';
import styled, { useTheme } from 'styled-components';
import DefaultUptimeTheme from 'component/theme/defaultTheme';
import { DeviceHealthStatus } from './types';
import { PerformanceConfig } from 'models/Device';

type Props = {
  performance: string;
  chipProps?: ChipProps;
  performanceConfig: PerformanceConfig;
  localeText?: any;
};

const Icon = ({ iconName, props }: { iconName: string; props: IconProps }) => {
  if (iconName?.trim()) {
    const Component = MuiIcon[iconName];
    return <Component {...props} />;
  }

  return <></>;
};

export const StyledLabelHealthyChip = styled(Typography)`
  ${({}) => `
    overflow: hidden;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    white-space: nowrap;
    text-overflow: ellipsis;
  `}
`;

const HealthyChip = ({
  performance,
  performanceConfig,
  chipProps = {},
  localeText
}: Props) => {
  const {
    variant = 'text',
    isShowTooltip = true,
    tooltipTitle,
    iconName = "Visibility",
    onClickIcon = () => {},
  } = performanceConfig ?? {};

  const theme = useTheme() as typeof DefaultUptimeTheme;

  const devicePerformanceConfig = {
  [DeviceHealthStatus.VERY_GOOD]: {
    colors: {
      background: theme.uptimeColors.deviceHealthEyebrow.veryGood?.background,
      text: theme.uptimeColors.deviceHealthEyebrow.veryGood?.color,
      icon: theme.uptimeColors.deviceHealthEyebrow.veryGood?.color
    },
    label: localeText ? localeText?.['VERY GOOD'] :  'VERY GOOD',
  },
  [DeviceHealthStatus.GOOD]: {
    colors: {
      background: theme.uptimeColors.deviceHealthEyebrow.good?.background,
      text: theme.uptimeColors.deviceHealthEyebrow.good?.color,
      icon: theme.uptimeColors.deviceHealthEyebrow.good?.color,
    },
    label: localeText ? localeText?.['VERY GOOD'] : 'VERY GOOD',
  },
  [DeviceHealthStatus.SUFFICIENT]: {
    colors: {
      background: theme.uptimeColors.deviceHealthEyebrow.sufficient?.background,
      text: theme.uptimeColors.deviceHealthEyebrow.sufficient?.color,
      icon: theme.uptimeColors.deviceHealthEyebrow.sufficient?.color,
    },
    label: localeText ? localeText?.['SUFFICIENT'] : 'SUFFICIENT',
  },
  [DeviceHealthStatus.AVERAGE]: {
    colors: {
      background: theme.uptimeColors.deviceHealthEyebrow.average?.background,
      text: theme.uptimeColors.deviceHealthEyebrow.average?.color,
      icon: theme.uptimeColors.deviceHealthEyebrow.average?.color,
    },
    label: localeText ? localeText?.['NEED ATTENTION'] : 'NEED ATTENTION',
  },
  [DeviceHealthStatus.NEED_ATTENTION]: {
    colors: {
      background: theme.uptimeColors.deviceHealthEyebrow.needAttention?.background,
      text: theme.uptimeColors.deviceHealthEyebrow.needAttention?.color,
      icon: theme.uptimeColors.deviceHealthEyebrow.needAttention?.color,
    },
    label: localeText ? localeText?.['NEED ATTENTION'] : 'NEED ATTENTION',
  },
  [DeviceHealthStatus.POOR]: {
    colors: {
      background: theme.uptimeColors.deviceHealthEyebrow.poor?.background,
      text: theme.uptimeColors.deviceHealthEyebrow.poor?.color,
      icon: theme.uptimeColors.deviceHealthEyebrow.poor?.color,
    },
    label: localeText ? localeText?.['POOR'] : 'POOR',
  },
  [DeviceHealthStatus.VERY_POOR]: {
    colors: {
      background: theme.uptimeColors.deviceHealthEyebrow.veryPoor?.background,
      text: theme.uptimeColors.deviceHealthEyebrow.veryPoor?.color,
      icon: theme.uptimeColors.deviceHealthEyebrow.veryPoor?.color,
    },
    label: localeText ? localeText?.['POOR'] : 'POOR',
  },
  [DeviceHealthStatus.NO_DATA]: {
    colors: {
      background: theme.uptimeColors.deviceHealthEyebrow.noData?.background,
      text: theme.uptimeColors.deviceHealthEyebrow.noData?.color,
      icon: theme.uptimeColors.deviceHealthEyebrow.noData?.color,
    },
    label: localeText ? localeText?.['NO DATA'] : 'NO DATA',
  },
};

  const performanceKey = MAPPING_KEYS[performance?.toUpperCase()];
  const config = devicePerformanceConfig[performanceKey || 'NO DATA'];

  if (variant === 'icon') {
    return (
      <HeartIconContainer
        tooltipTitle={tooltipTitle}
        performance={performance}
        onClickIcon={onClickIcon}
      />
    );
  }

  return (
    <>
      <Chip
        sx={{ backgroundColor: config?.colors?.background, overflow: "hidden" }}
        size="small"
        label={
          <Stack direction="row" alignItems="center" gap="2px">
            <Tooltip title={config?.label}>
              <StyledLabelHealthyChip
                fontSize="12px"
                fontWeight={600}
                sx={{ color: config?.colors.text }}
                id="lbl-healthychip-label"
              >
                {config?.label}
              </StyledLabelHealthyChip>
            </Tooltip>
            {isShowTooltip && (
              <Tooltip title={tooltipTitle}>
                <IconButton
                  size="small"
                  onClick={onClickIcon}
                  id="btn-healthychip-click_icon"
                  aria-label="Healthy Chip Button"
                >
                  <Icon
                    iconName={iconName || 'Visibility'}
                    props={{
                      sx: {
                        fontSize: "1rem",
                        background: config?.colors?.icon,
                        padding: '1px',
                        borderRadius: '50%',
                        color: '#ffffff',
                    },
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        }
        {...chipProps}
      />
    </>
  );
};

export default HealthyChip;
